/* Copyright 2012-2019 The MathWorks, Inc. */

// TODO: Remove _CSSStateMixin dependency, as seen in SplitButton and ToggleSplitButton

/**
 * This is the widget corresponding to the GalleryItem model.
 * A gallery item can have two views just like a gallery category.
 * It can either be in an ICON view or a LIST view, based on the current view of the
 * gallery model object.
 */

define([
    "dojo/_base/declare",
    "dojo/_base/lang",
    "dojo/aspect",
    "dojo/dom-construct",
    "dojo/_base/kernel",
    "dojo/_base/window",
    "dojo/keys",
    "dojo/on",
    "dojo/mouse",
    "dojo/touch",
    "dijit/popup",
    "dijit/_WidgetBase",
    "dijit/_CssStateMixin",
    "dijit/_TemplatedMixin",
    "dojo/i18n!l10n/nls/toolstrip",
    "dojo/text!./templates/GalleryItem.html",
    "../../constants/gallery/GalleryViewConstants",
    "../../constants/gallery/ItemLocationConstants",
    "../../constants/TypeConstants",
    "mw-form/mixins/property/IconMixin",
    "../_TooltipMixin",
    "../_WidgetMixin",
    "mw-form/mixins/event/ChangeEventMixin",
    "mw-form/mixins/property/DisabledMixin",
    "mw-form/mixins/AccessibilityClickMixin",
    "MW/toolstrip/ToolstripUtils",
    "MW/toolstrip/mixins/property/GalleryItemDisabledMixin",
    "MW/toolstrip/mixins/property/MnemonicKeyNavDirectionMixin",
    "MW/toolstrip/mixins/property/RemovableMixin",
    "MW/toolstrip/mixins/property/IconMixin",
    "MW/toolstrip/mixins/property/QuickAccessIconMixin",
    "MW/toolstrip/mixins/property/IconOverrideMixin",
    "MW/uiframework/widgets/ContextMenuButton",
    "mw-form/mixins/mixinDependencyValidator"
], function (declare, lang, aspect, domConstruct, kernel, win, keys, on, mouse, touch, popup, _WidgetBase,
             _CssStateMixin, _TemplatedMixin, toolstripL10n, template, GalleryViewConstants, ItemLocationConstants, TypeConstants,
             IconMixin, _TooltipMixin, _WidgetMixin, ChangeEventMixin, DisabledMixin, AccessibilityClickMixin,
             ToolstripUtils, GalleryItemDisabledMixin, MnemonicKeyNavDirectionMixin, RemovableMixin,
             TSIconMixin, QuickAccessIconMixin, IconOverrideMixin, ContextMenuButton, mixinDependencyValidator) {

    return declare(mixinDependencyValidator.validate([_WidgetBase, _CssStateMixin, _TemplatedMixin, IconMixin, _TooltipMixin, _WidgetMixin,
        ChangeEventMixin, DisabledMixin, AccessibilityClickMixin, GalleryItemDisabledMixin, MnemonicKeyNavDirectionMixin, RemovableMixin,
        TSIconMixin, QuickAccessIconMixin, IconOverrideMixin]), {

        baseClass: "mwWidget mwGalleryItem",
        templateString: template,
        galleryItemRowCount: 1,

        /**
         * Represents a Gallery Item Widget. A gallery item can have two views just like a
         * gallery category. It can either be in an ICON view or a LIST view, based on the current
         * view of the Gallery Popup.
         */
        constructor: function (args) {
            args = args || {};

            /**
             * A flag which determines whether this item is disabled or not. If an item is disabled,
             * it's opacity is reduced and it is not clickable.
             * @type boolean
             * @default false
             */
            this.disabled = !!args.disabled;

            this._favButtonClickConnection = null;

            this._handleFavButtonClick = lang.hitch(this, function () {
                var galleryCategory = this.getParent();
                this.favButtonPushed();

                // For column major layout (currently only supported for widgets other than gallery that open
                // GalleryPopup), when any item is favorited or defavorited, update the layout again to make sure
                // the Gallery Items grid within the category are correctly layed out.
                if (galleryCategory) {
                    var galleryPopup = galleryCategory.getParent();
                    if (galleryPopup && !galleryPopup._invokingGallery && galleryPopup.get("columnMajorLayout") && galleryPopup.updateColumnMajorLayout) {
                        galleryPopup.updateColumnMajorLayout();
                    }
                }
            });
        },

        /**
         * @override
         */
        postCreate: function () {
            var that = this;

            this.own(on(this.itemWrapperNode, 'click', function () {
                if (!that.disabled) {
                    that.itemPushed();
                }
            }));

            // overriding the disabled node to exclude the favorite button node.
            this._disabledNode = this.focusNode;

            this.inherited(arguments);

            this.attachFavButtonClickHandler();
            this._updateFavoriteButtonTitle();
            this.own(on(this.favButtonNode, "keydown", this._handleKeyDown.bind(this)));

            // Build 'Options' affordance the first time that the Gallery Item is hovered or focused
            // TODO: Find a better way to add the handlers so that we don't add them when "optionsEnabled" is false
            this.own(
                this._mouseOverListener = on(this.domNode, "mouseover", this._buildOptionsButtonIfOptionsEnabled.bind(this)),
                this._focusInListener = on(this.domNode, "focusin", this._buildOptionsButtonIfOptionsEnabled.bind(this))
            );
        },

        _handleKeyDown: function (e) {
            if (e.keyCode === keys.ENTER || e.keyCode === keys.SPACE) {
                this._handleFavButtonKeyPress(e);
            }
        },

        _handleFavButtonKeyPress: function (e) {
            var parent = this.getParent();

            // Handle this only in Favorite Category because that's where defavoriting a galleryItem destroys that
            // DOM node and the focus is lost.
            if (parent && parent.isFavoriteCategory) {
                var children = parent.getChildren();
                if (children) {
                    var index = children.indexOf(this);
                    var nextChild = children[index+1];

                    if (nextChild) {
                        if (nextChild.focusNode.tabIndex === 0) {
                            // Focus the next focusable gallery item
                            nextChild.focusNode.focus();
                        } else {
                            // Focus the favButtonNode of the next disabled gallery item
                            nextChild.favButtonNode.focus();
                        }
                    } else {
                        // Focus the next category header when the last gallery item in the favorites category is
                        // defavorited
                        var grandParent = parent.getParent();
                        if (grandParent) {
                            var categories = grandParent.getChildren();
                            if (categories) {
                                var firstCategory = categories[0];
                                if (firstCategory && firstCategory.titleNode) {
                                    firstCategory.titleNode.focus();
                                }
                            }
                        }
                    }
                }
            }

            // If the gallery item is disabled, the accessibilityClickMixin will not fire a click event. Explicitly
            // fire a click event in that case.
            if (this.get("disabled")) {
                on.emit(this.favButtonNode, "click", {
                    bubbles: true,
                    cancelable: true,
                    mwEventData: {
                        keyCode: e.keyCode
                    },
                    _dojo_click: true
                });
            }
        },

        _setIsFavoriteAttr: function (value) {
            if (value) {
                this.domNode.classList.add("isFavorite");
            } else {
                this.domNode.classList.remove("isFavorite");
            }
            this._set("isFavorite", value);
            this._updateFavoriteButtonTitle();
        },

        _updateFavoriteButtonTitle: function () {
            if (this.get("isFavorite")) {
                this.favButtonNode.title = toolstripL10n.favStarRemove;
            } else {
                this.favButtonNode.title = toolstripL10n.favStarAdd;
            }
        },

        _buildOptionsButtonIfOptionsEnabled: function() {
            // If the GalleryPanel has "optionsEnabled" set to true, build an 'Options' affordance
            if(this.domNode.matches('.mwOptionsEnabled *')) {
                // Only build the affordance widget if it doesn't exist yet
                if(!this._optionsButton) {
                    this._optionsButton = new ContextMenuButton({});
                    this._optionsButton.placeAt(this.optionsNode);
                    this._optionsButton.startup();
                }
            }

            // After we have checked whether or not to build the affordance, destroy unnecessary
            // listeners. Note that the "optionsEnabled" property must currently be set during the
            // construction of the Gallery Panel. To support dynamic modification of the
            // "optionsEnabled" property, remove this logic.
            this._mouseOverListener.remove();
            this._focusInListener.remove();
        },

        _setDisplayStateAttr: function (value) {
            // See g1362800.
            // The Gallery Item in the Gallery and the Gallery Popup are backed by the same peer node. When the item's
            // checked property changes that causes a refresh of the Gallery to happen, which results in the creation of
            // new Gallery Items in the Gallery. Now, the Gallery sets the displayState of any children to be icon_view
            // which triggers a change in the corresponding peer node. This peer node also backs the gallery item in the
            // gallery popup and hence ends up changing the display state of the item to icon_view even if the ancestor
            // gallery is in list_view. Be robust to this scenario.
            var parent = this.getParent();
            var grandParent = parent && parent.getParent();
            if (grandParent
                && grandParent.get("type") === TypeConstants.GALLERY_POPUP
                && grandParent.get("displayState") !== value) {
                return;
            }

            this._set("displayState", value);
            this._updateItemDimensions();
            this._updateText();
            if (this._getIconType(this.icon) === "DATA_URI") {
                this._updateIconNodeSize();
            }
        },

        _updateItemDimensions: function () {
            if (this.displayState === GalleryViewConstants.LIST) {
                this.domNode.style.width = "auto";
            } else {
                if (this._getPropertyFromAncestor("galleryItemWidth")) {
                    this.domNode.style.width = this._getPropertyFromAncestor("galleryItemWidth") + "px";
                } else {
                    this.domNode.style.width = "";
                }
            }
        },

        /**
         * Attach the callback for the click action on the Favorite (Star) button located on the
         * top right corner of the item.
         */
        attachFavButtonClickHandler: function () {
            var that = this;

            this._favButtonClickConnection = on(this.favButtonNode, 'click', this._handleFavButtonClick);
        },

        /**
         * Attach the callback for the click action on the Favorite (Star) button located on the
         * top right corner of the item.
         */
        detachFavButtonClickHandler: function () {
            if (this._favButtonClickConnection) {
                this._favButtonClickConnection.remove();
                this._favButtonClickConnection = null;
            }
        },

        /**
         * A setter for the label attribute.
         * @param {string} value The label of the Control to be displayed to the user.
         */
        _setTextAttr: function (value) {
            this._set("text", value);

            this._updateText();
        },

        _setTextOverrideAttr: function (value) {
            this._set("textOverride", value);

            this._updateText();
        },

        _updateText: function () {
            var text;

            if (this.textOverride) {
                text = this.textOverride;
            } else if (this.text) {
                text = this.text;
            }

            if (!text) {
                return;
            }

            if (this.displayState === GalleryViewConstants.LIST) {
                // The LIST view has plenty of space for the label to be displayed in a single line.
                text = text.replace(/\n/g, " ");
            }

            // g1892451: For Japanese locale, remove new line character "\n" to be able to better read the text in icon
            // view.
            if (kernel.locale === "ja-jp" && this.displayState === GalleryViewConstants.ICON) {
                // g1953400: Only remove new line character "\n" when the entire text is Japanese. Do not remove the
                // new line character "\n" when the GalleryItem text contains English characters.
                var regExp = /[A-Za-z]+/; // regex to find at least one occurrence of any english character(s).
                if (!regExp.test(text)) {
                    text = text.replace(/\n/g, ""); // Entire text is japanese, remove "\n"
                }
            }

            this.labelNode.textContent = text;

            this._updateTooltip();
        },

        _getLocation: function () {
            var parentWidget = this.getParent(), result = false;

            if (parentWidget) {
                switch (parentWidget.type) {
                case TypeConstants.GALLERY_CATEGORY:
                    result = ItemLocationConstants.CATEGORY;
                    break;
                case TypeConstants.GALLERY_FAVORITE_CATEGORY:
                    result = ItemLocationConstants.FAVORITES;
                    break;
                case TypeConstants.GALLERY:
                    result = ItemLocationConstants.GALLERY;
                    break;
                }
            }

            return result;
        },

        _getPropertyFromAncestor: function (prop) {
            var result = false, location = this._getLocation();
            if (location === ItemLocationConstants.GALLERY) {
                result = this.getParent()[prop];
            } else if (location === ItemLocationConstants.CATEGORY || location === ItemLocationConstants.FAVORITES) {
                if (this.getParent().getParent()) {
                    result = this.getParent().getParent()[prop];
                }
            }
            return result;
        },

        itemPushed: function () {
            if (this.get("closePopupOnClick") !== false) {
                popup.close();
            }
        },

        favButtonPushed: function () {
        },

        _setIconAttr: function (value) {
            this.inherited(arguments);
            //Added this class in IconMixin for all icons
            // Do not need for gallery items
            this.iconNode.classList.remove("dijitInline");
            /* Apply only for data uri */
            if (this._getIconType(value) === "DATA_URI") {
                this._updateIconNodeSize();
            }
        },

        _updateIconNodeSize: function () {
            var img, galleryItemWidth, widthToSet, iconNode;
            if (this.icon && this._getPropertyFromAncestor("galleryItemRowCount") === 1) {
                iconNode = this.iconNode;
                img = domConstruct.create("img", {
                    src: this.icon.replace(/"/g, "").replace(/url\(|\)$/ig, "")
                });
                galleryItemWidth = this._getPropertyFromAncestor("galleryItemWidth");
                aspect.after(img, "onload", function () {
                    widthToSet = img.width > galleryItemWidth - 4 ? galleryItemWidth - 4 : img.width;
                    iconNode.style.width =  widthToSet + "px";
                    iconNode.style.height = img.height + "px";
                });
            }
        },

        _updateDOM: function () {
            var value;

            this._removeOldIcon();

            // To avoid as much icon flashing as possible, determine iconOverride synchronously
            if (this.iconOverride !== "") {
                value = this.iconOverride;
            } else if (this.iconProp && (this.iconProp !== "icon") && this[this.iconProp]) {
                value = this[this.iconProp];
            } else if ((this.get("galleryItemRowCount") > 1) && (this.quickAccessIcon !== "")) {
                value = this.quickAccessIcon;
            } else {
                value = this.icon;
            }

            this._applyNewIcon(value);
        },

        _setGalleryItemRowCountAttr: function (value) {
            this._set("galleryItemRowCount", value);
            this._updateDOM();
        },

        isEligibleForQab: function () {
            return this._getPropertyFromAncestor("qabEligible");
        },

        galleryStateChanged: function (fireEventOnGalleryPopupOrPanel) {
            var parentWidget = this.getParent();
            if (parentWidget && parentWidget.galleryStateChanged) {
                parentWidget.galleryStateChanged(fireEventOnGalleryPopupOrPanel);
            }
        },

        _qeClick: function () {
            this.itemPushed();
        },

        _qeFavoriteClick: function () {
            this._handleFavButtonClick();
        },

        // Returns true if the 'Options' button has been built
        _qeOptionsButtonBuilt: function() {
            return Boolean(this._optionsButton);
        },

        // Performs the "click" action on the 'Options' button
        _qeOptionsButtonClick: function() {
            this._optionsButton.domNode.dispatchEvent(new MouseEvent("click", {
                bubbles: true,
                cancelable: true
            }));
        }
    });
});